import React, {
    ChangeEvent,
    DragEvent,
    KeyboardEvent,
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    Box,
    Button,
    IconButton,
    LinearProgress,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import CheckCircle from '@mds/icons/CheckCircle';
import Trash from '@mds/icons/Trash';
import UploadIcon from '@mds/icons/Upload';
import WarningCircle from '@mds/icons/WarningCircle';

interface DropzoneInstance {
    on: (
        event: 'success' | 'removedfile',
        handler: (...args: any[]) => void
    ) => void;
    displayExistingFile: (file: {
        name: string;
        status?: string;
        dataUrl?: string;
    }) => void;
}

interface DropzoneConfig extends Partial<DropzoneInstance> {
    headers?: Record<string, string>;
    init?: (this: DropzoneInstance) => void;
}

type UploadState = 'idle' | 'dragging' | 'uploading' | 'success' | 'error';

export const Dropzone = ({
    url,
    config = {},
    description,
    acceptedFiles,
    isSuccess,
    maxFilesize,
}: {
    url: string;
    config?: DropzoneConfig;
    description?: ReactNode;
    acceptedFiles?: string;
    isSuccess?: boolean;
    maxFilesize?: number;
    [key: string]: any;
}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const handlersRef = useRef<Record<string, Array<(...args: any[]) => void>>>(
        {}
    );
    const [fileName, setFileName] = useState('');
    const [uploadState, setUploadState] = useState<UploadState>(
        isSuccess ? 'success' : 'idle'
    );
    const [errorMessage, setErrorMessage] = useState('');

    const instance = useMemo<DropzoneInstance>(
        () => ({
            on: (event, handler) => {
                handlersRef.current[event] = handlersRef.current[event] ?? [];
                handlersRef.current[event].push(handler);
            },
            displayExistingFile: (file) => {
                setFileName(file.name);
                setUploadState('success');
                setErrorMessage('');
            },
        }),
        []
    );

    useEffect(() => {
        handlersRef.current = {};
        config.init?.call(instance);
    }, [config, instance]);

    const emit = useCallback((event: string, ...args: any[]) => {
        handlersRef.current[event]?.forEach((handler) => handler(...args));
    }, []);

    const chooseFile = useCallback(() => {
        if (uploadState === 'uploading') return;
        inputRef.current?.click();
    }, [uploadState]);

    const removeFile = useCallback(() => {
        if (inputRef.current) inputRef.current.value = '';
        setFileName('');
        setUploadState('idle');
        setErrorMessage('');
        emit('removedfile');
    }, [emit]);

    const isAcceptedFile = useCallback(
        (file: File) => {
            if (!acceptedFiles) return true;

            return acceptedFiles
                .split(',')
                .map((fileType) => fileType.trim().toLowerCase())
                .some((fileType) => {
                    const fileName = file.name.toLowerCase();
                    const mimeType = file.type.toLowerCase();

                    if (fileType.startsWith('.'))
                        return fileName.endsWith(fileType);
                    if (fileType.endsWith('/*'))
                        return mimeType.startsWith(fileType.replace('*', ''));
                    return mimeType === fileType;
                });
        },
        [acceptedFiles]
    );

    const uploadFile = useCallback(
        async (file: File) => {
            if (!isAcceptedFile(file)) {
                setFileName('');
                setUploadState('error');
                setErrorMessage('File format not supported');
                return;
            }

            if (maxFilesize && file.size > maxFilesize * 1024 * 1024) {
                setFileName('');
                setUploadState('error');
                setErrorMessage(`Maximum file size is ${maxFilesize}MB`);
                return;
            }

            setFileName(file.name);
            setUploadState('uploading');
            setErrorMessage('');

            const formData = new FormData();
            formData.append('file', file);

            try {
                const response = await fetch(url, {
                    method: 'POST',
                    headers: config.headers,
                    body: formData,
                });

                if (!response.ok) {
                    throw new Error('Upload failed');
                }

                const body = await response.json();
                setUploadState('success');
                emit('success', file, body);
            } catch (error) {
                setUploadState('error');
                setErrorMessage('Upload failed. Try again.');
            }
        },
        [config.headers, emit, isAcceptedFile, maxFilesize, url]
    );

    const handleInputChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];
            if (!file) return;
            uploadFile(file);
        },
        [uploadFile]
    );

    const handleDrop = useCallback(
        (event: DragEvent<HTMLElement>) => {
            event.preventDefault();
            event.stopPropagation();

            const file = event.dataTransfer.files?.[0];
            if (!file) {
                setUploadState(fileName ? 'success' : 'idle');
                return;
            }

            uploadFile(file);
        },
        [fileName, uploadFile]
    );

    const handleDragEnter = useCallback(
        (event: DragEvent<HTMLElement>) => {
            event.preventDefault();
            event.stopPropagation();
            if (uploadState !== 'uploading') setUploadState('dragging');
        },
        [uploadState]
    );

    const handleDragLeave = useCallback(
        (event: DragEvent<HTMLElement>) => {
            event.preventDefault();
            event.stopPropagation();
            if (event.currentTarget.contains(event.relatedTarget as Node))
                return;
            setUploadState(fileName ? 'success' : 'idle');
        },
        [fileName]
    );

    const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLElement>) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            chooseFile();
        },
        [chooseFile]
    );

    const hasFile = Boolean(fileName) || isSuccess;
    const state = uploadState === 'idle' && isSuccess ? 'success' : uploadState;
    const message =
        state === 'success'
            ? 'Upload complete'
            : state === 'error'
              ? errorMessage
              : 'Drag file or click here to upload';
    const icon =
        state === 'success' ? (
            <CheckCircle color="success" />
        ) : state === 'error' ? (
            <WarningCircle color="error" />
        ) : (
            <UploadIcon />
        );

    return (
        <Paper
            variant="outlined"
            role="button"
            tabIndex={0}
            onClick={chooseFile}
            onKeyDown={handleKeyDown}
            onDrop={handleDrop}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragEnter}
            onDragLeave={handleDragLeave}
            sx={{
                p: 3,
                cursor: state === 'uploading' ? 'default' : 'pointer',
                borderStyle: 'dashed',
                borderColor:
                    state === 'error'
                        ? 'error.main'
                        : state === 'dragging'
                          ? 'primary.main'
                          : 'divider',
                bgcolor:
                    state === 'dragging' ? 'action.hover' : 'background.paper',
                textAlign: 'center',
                transition: (theme) =>
                    theme.transitions.create([
                        'background-color',
                        'border-color',
                    ]),
                '&:focus-visible': {
                    outline: 2,
                    outlineColor: 'primary.main',
                    outlineOffset: 2,
                },
            }}
        >
            <Stack
                spacing={2}
                sx={{
                    alignItems: 'center',
                }}
            >
                <Box>{icon}</Box>
                <Stack
                    spacing={0.5}
                    sx={{
                        alignItems: 'center',
                    }}
                >
                    <Typography variant="body1">{message}</Typography>
                    {description && state !== 'success' && (
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'text.secondary',
                            }}
                        >
                            {description}
                        </Typography>
                    )}
                </Stack>
                <input
                    ref={inputRef}
                    type="file"
                    accept={acceptedFiles}
                    onChange={handleInputChange}
                    hidden
                />
                {state === 'uploading' && (
                    <LinearProgress sx={{ width: '100%' }} />
                )}
                {hasFile ? (
                    <Paper
                        variant="outlined"
                        onClick={(event) => event.stopPropagation()}
                        sx={{ width: '100%', p: 1.5, bgcolor: 'action.hover' }}
                    >
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                                alignItems: 'center',
                            }}
                        >
                            <Box
                                sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}
                            >
                                <Typography
                                    variant="caption"
                                    title={fileName}
                                    sx={{
                                        display: 'block',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {fileName}
                                </Typography>
                            </Box>
                            <IconButton
                                size="small"
                                aria-label="Remove file"
                                onClick={removeFile}
                            >
                                <Trash fontSize="small" />
                            </IconButton>
                        </Stack>
                    </Paper>
                ) : (
                    <Button
                        size="small"
                        variant="contained"
                        onClick={(event) => {
                            event.stopPropagation();
                            chooseFile();
                        }}
                    >
                        Choose file
                    </Button>
                )}
            </Stack>
        </Paper>
    );
};
